<template>
    <FormSection title="Referral Source" :full-height="false" :application-id="referral.application">
        <template v-slot:content>
            <section id="Referral_Source" class="row p-4 pb-0 g-0">
                <div class="col-12 col-lg-6 pe-4">
                    <div class="form-group col-12">
                        <label for="referral-types"> Referral Type <span class="text-danger">*</span></label>
                        <select
                            class="form-select"
                            v-model="referral.referralType"
                            @change="refTypeChanged(referral.referralType)"
                            @blur="v$.referral.referralType.$touch"
                            :class="`${!v$.referral.referralType.$error ? '' : 'field-invalid'}`"
                        >
                            <option :value="null">--Select Referral Type--</option>
                            <option
                                v-for="[key, value] in enumToMap(referralTypesEnum)"
                                :value="value.value"
                                :key="key"
                            >
                                {{ value.name }}
                            </option>
                        </select>
                        <span v-if="v$.referral.referralType.$error" class="field-invalid"
                            >Referral Type is required.</span
                        >
                    </div>
                    <div class="form-group col-12">
                        <div v-if="showRefSourceTypeahead">
                            <label for="referral-source-physician">
                                Referral Source <span class="text-danger">*</span></label
                            >
                            <TypeaheadInput
                                :getData="getList"
                                :value="referralSourceTypeahead"
                                v-on:valueChanged="changeTeamMember"
                                :placeholder="referralSourcePlaceholder"
                                v-on:blurredInput="v$.referral.referralSource.$touch"
                                :class="`${!v$.referral.referralSource?.$error ? '' : 'field-invalid'}`"
                            />
                        </div>
                        <div v-if="showReferralSource">
                            <label for="referral-source-family">
                                Referral Source <span class="text-danger">*</span></label
                            >
                            <input
                                type="text"
                                class="form-control"
                                v-model="referral.referralSource"
                                :placeholder="referralSourcePlaceholder"
                                @blur="v$.referral.referralSource.$touch"
                                :class="`${!v$.referral.referralSource.$error ? '' : 'field-invalid'}`"
                            />
                        </div>
                        <span
                            v-if="
                                (referralSourceValidation && referral.referralSource == null) ||
                                v$.referral.referralSource?.$error
                            "
                            class="field-invalid"
                            >Referral Source is required.</span
                        >
                    </div>
                    <div class="form-group col-12" v-if="showReferralDetails">
                        <label for="referral-details"> Referral Details <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="referral.referralDetails"
                            placeholder="Enter caller/referral details"
                            @blur="v$.referral.referralDetails.$touch"
                            :class="`${!v$.referral.referralDetails.$error ? '' : 'field-invalid'}`"
                        />
                        <span
                            v-if="
                                (referralDetailsValidation && referral.referralDetails == null) ||
                                v$.referral.referralDetails.$error
                            "
                            class="field-invalid"
                            >Referral Details are required.</span
                        >
                    </div>
                </div>

                <div class="col-12 col-lg-6">
                    <div class="form-group col-12">
                        <label for="referral-date"> Referral Date <span class="text-danger">*</span> </label>
                        <DatePicker :getDate="changeDate">
                            <div class="input-group date datepickerInput">
                                <input
                                    placeholder="MM/DD/YYYY"
                                    type="text"
                                    class="form-control"
                                    name="referralDate"
                                    @blur="v$.referral.referralDate.$touch"
                                    :class="`${!v$.referral.referralDate.$error ? '' : 'field-invalid'}`"
                                    :value="formatDateInput(referral.referralDate)"
                                    @change="handleDateChange"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                            </div>
                        </DatePicker>
                        <span v-if="v$.referral.referralDate.$error" class="field-invalid"
                            >Referral Date is required.</span
                        >
                    </div>
                    <div class="form-group col-12">
                        <label for="start-of-care-date">
                            Requested Start of Care Date
                            <span class="text-danger" v-if="!referral.isNonMedical">*</span>
                        </label>
                        <DatePicker :getDate="changeDate">
                            <div class="input-group date datepickerInput">
                                <input
                                    placeholder="MM/DD/YYYY"
                                    name="referralSOCDate"
                                    type="text"
                                    class="form-control"
                                    @blur="v$.referral.requestedStartOfCareDate.$touch"
                                    :class="`${!v$.referral.requestedStartOfCareDate.$error ? '' : 'field-invalid'}`"
                                    :value="formatDateInput(referral.requestedStartOfCareDate)"
                                    @change="handleDateChange"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                            </div>
                        </DatePicker>
                        <span v-if="v$.referral.requestedStartOfCareDate.$error" class="field-invalid">{{
                            v$.referral.requestedStartOfCareDate.$errors[0].$message
                        }}</span>
                    </div>
                    <div class="form-group col-12" v-if="!referral.isNonMedical">
                        <label for="admission-source">
                            Admission Source
                            <a tabindex="0" href="javascript:void(0);" role="button">
                                <i class="fas fa-info-circle text-primary font-size-xs"></i>
                            </a>
                        </label>
                        <select class="form-select" v-model="referral.admissionSource">
                            <option :value="null">--Select Admission Source--</option>
                            <option
                                v-for="[key, value] in enumToMap(admissionSourcesEnum)"
                                :value="value.value"
                                :key="key"
                            >
                                {{ value.name }}
                            </option>
                        </select>
                    </div>
                    <div class="form-group col-12" v-if="!referral.isNonMedical">
                        <label for="admission-type">
                            Admission Type
                            <a
                                tabindex="0"
                                href="javascript:void(0);"
                                role="button"
                                v-custom-popover="{
                                    content: notSelectedTooltip,
                                    className: 'w-25 fw-sm info-popup'
                                }"
                            >
                                <i class="fas fa-info-circle text-primary font-size-xs"></i>
                            </a>
                        </label>
                        <select class="form-select" v-model="referral.admissionType">
                            <option :value="null">--Select Admission Type--</option>
                            <option
                                v-for="[key, value] in enumToMap(admissionSourcesEnum)"
                                :value="value.value"
                                :key="key"
                            >
                                {{ value.name }}
                            </option>
                        </select>
                    </div>
                    <div class="form-group col-12">
                        <label for="marketer"> Marketer </label>
                        <select class="form-select" v-model="referral.internalReferral">
                            <option :value="null">--Select User--</option>
                            <option v-for="user in users" :value="user.value" :key="user">
                                {{ user.name }}
                            </option>
                        </select>
                    </div>
                </div>
            </section>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./ReferralSourceInformation.ts"></script>
